import {
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from "firebase/firestore";
import { locationIdForFirestore, parseStoredLocationId } from "./locationId";
import {
  Lead,
  LeadBusinessContactRole,
  LeadStatus,
  LeadShippingQuoteSnapshot,
  LeadShippingQuoteTruck,
  normalizeLeadTruckDropoffLocationIds,
  TruckRequest
} from "../leads/lead";

const LEAD_BUSINESS_CONTACT_ROLES: LeadBusinessContactRole[] = [
  "owner",
  "manager",
  "partner",
];

/** Maps stored role strings (including legacy values) to the current enum. */
const normalizeLeadBusinessContactRole = (
  raw: string,
): LeadBusinessContactRole | "" => {
  if (!raw) return "";
  if ((LEAD_BUSINESS_CONTACT_ROLES as string[]).includes(raw)) {
    return raw as LeadBusinessContactRole;
  }
  if (raw === "business_owner") return "owner";
  if (raw === "business_partner") return "partner";
  return "";
};

const normalizeBusinessContactForFirestore = (
  bc: Lead["businessContact"] | undefined
): Record<string, string | null> | null => {
  if (!bc) return null;
  const name = bc.name?.trim() || "";
  const email = bc.email?.trim() || "";
  const phone = bc.phone?.trim() || "";
  const role = bc.role?.trim() || "";
  if (!name && !email && !phone && !role) return null;
  return {
    name: name || null,
    email: email || null,
    phone: phone || null,
    role: role || null,
  };
};

const normalizeShippingQuoteSnapshot = (
  raw: unknown,
): LeadShippingQuoteSnapshot | null => {
  if (!raw || typeof raw !== "object") return null;
  const snapshot = raw as Record<string, unknown>;
  const trucksRaw = snapshot.trucks;
  if (!Array.isArray(trucksRaw)) return null;

  const trucks: LeadShippingQuoteTruck[] = trucksRaw
    .map((truck) => {
      if (!truck || typeof truck !== "object") return null;
      const t = truck as Record<string, unknown>;
      const vehicleId = typeof t.vehicleId === "string" ? t.vehicleId.trim() : "";
      if (!vehicleId) return null;
      return {
        vehicleId,
        vehicleLabel: typeof t.vehicleLabel === "string" ? t.vehicleLabel : "",
        stockNumber: typeof t.stockNumber === "string" ? t.stockNumber : "",
        vinLast4: typeof t.vinLast4 === "string" ? t.vinLast4 : "",
        inventoryAddress:
          typeof t.inventoryAddress === "string" ? t.inventoryAddress : "",
        currentLocationAddress:
          typeof t.currentLocationAddress === "string"
            ? t.currentLocationAddress
            : "",
        destinationAddress:
          typeof t.destinationAddress === "string" ? t.destinationAddress : "",
        inventoryToCurrentMiles:
          typeof t.inventoryToCurrentMiles === "number"
            ? t.inventoryToCurrentMiles
            : null,
        inventoryToCurrentCost:
          typeof t.inventoryToCurrentCost === "number"
            ? t.inventoryToCurrentCost
            : null,
        currentToDestinationMiles:
          typeof t.currentToDestinationMiles === "number"
            ? t.currentToDestinationMiles
            : null,
        currentToDestinationCost:
          typeof t.currentToDestinationCost === "number"
            ? t.currentToDestinationCost
            : null,
        miles: typeof t.miles === "number" ? t.miles : null,
        shippingCost: typeof t.shippingCost === "number" ? t.shippingCost : null,
      };
    })
    .filter((truck): truck is LeadShippingQuoteTruck => truck != null);

  const pricePerMile =
    typeof snapshot.pricePerMile === "number" ? snapshot.pricePerMile : null;
  if (pricePerMile == null) return null;

  return {
    pricePerMile,
    calculatedAt:
      typeof snapshot.calculatedAt === "string" ? snapshot.calculatedAt : "",
    quotedCount:
      typeof snapshot.quotedCount === "number" ? snapshot.quotedCount : trucks.length,
    totalMiles: typeof snapshot.totalMiles === "number" ? snapshot.totalMiles : 0,
    totalShippingCost:
      typeof snapshot.totalShippingCost === "number"
        ? snapshot.totalShippingCost
        : 0,
    trucks,
  };
};

export const leadConverter: FirestoreDataConverter<Lead> = {
  toFirestore: (lead: Lead) => {
    const { id, businessContact, ...data } = lead;

    // Helper function to convert undefined to null and dates to ISO strings
    const convertValue = (value: any) => {
      if (value === undefined) return null;
      if (value instanceof Date) return isNaN(value.getTime()) ? '' : value.toISOString();
      return value;
    };

    // Process truck request data
    let truckRequest = null;
    if (lead.truckRequest) {
      truckRequest = {
        notes: lead.truckRequest.notes,
        types: lead.truckRequest.types || [] // Initialize the types property
      };
      
      // Process truck types if they exist
      if (lead.truckRequest.types && Array.isArray(lead.truckRequest.types)) {
        truckRequest.types = lead.truckRequest.types.map(type => ({
          ...type,
          // Convert string values to appropriate types
          quantity: typeof type.quantity === 'string' ? parseInt(type.quantity) : type.quantity,
          minYear: typeof type.minYear === 'string' ? parseInt(type.minYear) : type.minYear,
          budget: typeof type.budget === 'string' ? 
            Number(String(type.budget).replace(/,/g, '')) : 
            type.budget,
          // Ensure array fields are always arrays
          make: Array.isArray(type.make) ? type.make : (type.make ? [type.make] : []),
          model: Array.isArray(type.model) ? type.model : (type.model ? [type.model] : []),
          fuelType: Array.isArray(type.fuelType) ? type.fuelType : (type.fuelType ? [type.fuelType] : [])
        }));
      }
    }

    // Process status history to ensure no undefined values
    const statusHistory = lead.statusHistory ? lead.statusHistory.map(history => ({
      ...history,
      id: history.id || null,
      previousStatus: history.previousStatus || null,
      newStatus: history.newStatus,
      changedBy: history.changedBy,
      changedAt: convertValue(history.changedAt),
      notes: history.notes || null
    })) : [];

    // Process email history to ensure dates are converted to ISO strings
    const emailHistory = lead.emailHistory ? lead.emailHistory.map(entry => ({
      ...entry,
      id: entry.id || null,
      type: entry.type,
      recipientEmail: entry.recipientEmail,
      sentAt: convertValue(entry.sentAt)
    })) : [];

    const {
      dropoffLocationId: _legacyDropoffLocationId,
      businessAddressId,
      companyId,
      client_id,
      contactId,
      truckDropoffLocationIds,
      ...rest
    } = data;

    const normalizedTruckDropoffs = normalizeLeadTruckDropoffLocationIds(
      truckDropoffLocationIds,
    );

    // Return the processed data
    return {
      ...rest,
      leadNumber: lead.leadNumber?.trim() || null,
      businessContact: normalizeBusinessContactForFirestore(businessContact),
      client_id: client_id?.trim() || null,
      contactId: contactId?.trim() || null,
      companyId: companyId?.trim() || null,
      truckDropoffLocationIds:
        Object.keys(normalizedTruckDropoffs).length > 0
          ? normalizedTruckDropoffs
          : null,
      businessAddressId: locationIdForFirestore(businessAddressId),
      truckRequest,
      statusHistory,
      emailHistory,
      createdAt: convertValue(lead.createdAt),
      updatedAt: convertValue(lead.updatedAt),
      lastContactDate: convertValue(lead.lastContactDate),
      nextFollowUpDate: convertValue(lead.nextFollowUpDate),
      lastActionDate: convertValue(lead.lastActionDate),
      status: lead.status || null,
      prevStatus: lead.prevStatus || null,
      source: lead.source || null,
      truckIds: lead.truckIds || [],
      applicationIds: lead.applicationIds || [],
      followUpNotes: lead.followUpNotes || [],
      tags: lead.tags || [],
      needs_shipping_information: lead.needs_shipping_information === true,
      shipping_quote_snapshot: lead.shipping_quote_snapshot ?? null,
      shipping_quote_notification_token:
        typeof lead.shipping_quote_notification_token === "number"
          ? lead.shipping_quote_notification_token
          : 0,
    };
  },

  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Lead => {
    const data = snapshot.data(options);

    // Convert ISO string dates or Firestore timestamps to JavaScript Date objects
    const convertTimestamp = (timestamp: any): Date | null => {
      if (!timestamp) return null;
      if (timestamp instanceof Timestamp) return timestamp.toDate();
      if (typeof timestamp === 'string') return new Date(timestamp);
      return null;
    };

    // Process truck request data
    let truckRequest: TruckRequest = {
      notes: data.truckRequest?.notes || "",
      types: undefined // Initialize the types property
    };
    
    if (data.truckRequest) {
      // If types already exists, use it
      if (data.truckRequest.types && Array.isArray(data.truckRequest.types)) {
        truckRequest.types = data.truckRequest.types.map((type: any) => ({
          type: type.type || "",
          size: type.size || "",
          condition: type.condition || "",
          quantity: type.quantity || 1,
          // Handle array fields properly
          make: Array.isArray(type.make) ? type.make : (type.make ? [type.make] : []),
          model: Array.isArray(type.model) ? type.model : (type.model ? [type.model] : []),
          minYear: type.minYear || undefined,
          maxYear: type.maxYear || undefined,
          fuelType: Array.isArray(type.fuelType) ? type.fuelType : (type.fuelType ? [type.fuelType] : []),
          maxMileage: type.maxMileage || undefined,
          budget: type.budget || undefined
        }));
      } 
    }

    // Process email history - keep sentAt as string to match the interface
    const emailHistory = Array.isArray(data.emailHistory) 
      ? data.emailHistory.map((entry: any) => ({
          ...entry,
          id: entry.id || "",
          type: entry.type || "suggested",
          recipientEmail: entry.recipientEmail || "",
          sentAt: entry.sentAt || new Date().toISOString()
        })) 
      : [];

    const rawBc = data.businessContact;
    let businessContact: Lead["businessContact"] = undefined;
    if (rawBc != null && typeof rawBc === "object" && !Array.isArray(rawBc)) {
      const name = typeof rawBc.name === "string" ? rawBc.name.trim() : "";
      const email = typeof rawBc.email === "string" ? rawBc.email.trim() : "";
      const phone = typeof rawBc.phone === "string" ? rawBc.phone.trim() : "";
      const rawRole = typeof rawBc.role === "string" ? rawBc.role.trim() : "";
      const role = normalizeLeadBusinessContactRole(rawRole);
      if (name || email || phone || role) {
        businessContact = {
          ...(name ? { name } : {}),
          ...(email ? { email } : {}),
          ...(phone ? { phone } : {}),
          ...(role ? { role: role as LeadBusinessContactRole } : {}),
        };
      }
    }

    // Convert ISO string dates to Date objects
    return {
      id: snapshot.id,
      leadNumber:
        typeof data.leadNumber === "string" && data.leadNumber.trim()
          ? data.leadNumber.trim()
          : null,
      customerName: data.customerName || "",
      email: data.email || "",
      phone: data.phone || "",
      client_id:
        typeof data.client_id === "string" && data.client_id.trim()
          ? data.client_id.trim()
          : null,
      contactId:
        typeof data.contactId === "string" && data.contactId.trim()
          ? data.contactId.trim()
          : null,
      businessContact,
      status: (data.status || 'new') as LeadStatus,
      prevStatus: data.prevStatus as LeadStatus | null,
      assignedRepId: data.assignedRepId || 0,
      company: data.company || "",
      companyId:
        typeof data.companyId === "string" && data.companyId.trim()
          ? data.companyId.trim()
          : null,
      location: data.location || "",
      truckDropoffLocationIds: normalizeLeadTruckDropoffLocationIds(
        data.truckDropoffLocationIds,
      ),
      ...(() => {
        const dropoffLocationId = parseStoredLocationId(
          data.dropoffLocationId,
          data.dropoffLocation,
        );
        return dropoffLocationId ? { dropoffLocationId } : {};
      })(),
      ...(() => {
        const businessAddressId = parseStoredLocationId(
          data.businessAddressId,
          data.businessAddress,
        );
        return businessAddressId ? { businessAddressId } : {};
      })(),
      financingStatus: data.financingStatus || "",
      timeFrame: data.timeFrame || "",
      vehicleInterest: data.vehicleInterest || "",
      truckIds: Array.isArray(data.truckIds) ? data.truckIds : [],
      truckRequest,
      followUpNotes: Array.isArray(data.followUpNotes) ? data.followUpNotes : [],
      statusHistory: Array.isArray(data.statusHistory) ? data.statusHistory.map((history: any) => ({
        ...history,
        changedAt: convertTimestamp(history.changedAt) || new Date()
      })) : [],
      emailHistory,
      applicationIds: Array.isArray(data.applicationIds) ? data.applicationIds : [],
      createdAt: convertTimestamp(data.createdAt) || new Date(),
      updatedAt: convertTimestamp(data.updatedAt) || new Date(),
      lastContactDate: convertTimestamp(data.lastContactDate),
      nextFollowUpDate: convertTimestamp(data.nextFollowUpDate),
      source: data.source || null,
      priority: data.priority || "medium",
      tags: Array.isArray(data.tags) ? data.tags : [],
      createdBy: data.createdBy || "",
      lastActionDate: convertTimestamp(data.lastActionDate),
      needs_shipping_information: data.needs_shipping_information === true,
      shipping_quote_snapshot: normalizeShippingQuoteSnapshot(
        data.shipping_quote_snapshot,
      ),
      shipping_quote_notification_token:
        typeof data.shipping_quote_notification_token === "number"
          ? data.shipping_quote_notification_token
          : 0,
    };
  },
};
